<template>
    <div class="tw:space-y-2">
        <!-- Drop zone — siempre muestra el mensaje de subida -->
        <div
            ref="dropzoneRef"
            tabindex="0"
            :class="zoneClasses"
            @dragover.prevent="onDragOver"
            @dragenter.prevent="onDragEnter"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
            @click="triggerFileInput"
            @keydown.enter.prevent="triggerFileInput"
            @keydown.space.prevent="triggerFileInput"
        >
            <input
                ref="fileInput"
                type="file"
                :accept="accept"
                class="tw:hidden"
                @change="onFileSelected"
            />

            <!-- Upload prompt: cloud icon + call to action -->
            <div class="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:py-8 tw:text-gray-400">
                <svg
                    class="tw:w-10 tw:h-10"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                    />
                </svg>
                <p class="tw:text-sm tw:font-medium">Arrastra una imagen o haz clic para subir</p>
                <p class="tw:text-xs">{{ formatAccept }} — Máx. {{ formattedMaxSize }}</p>
            </div>
        </div>

        <!-- Info del archivo seleccionado debajo del dropzone -->
        <div v-if="fileName" class="tw:flex tw:items-center tw:gap-2 tw:mt-4 tw:px-4 tw:py-2 tw:bg-gray-50 tw:rounded-lg tw:border tw:border-gray-200">
            <svg class="tw:w-5 tw:h-5 tw:text-gray-400 tw:shrink-0" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <span class="tw:text-sm tw:text-gray-700 tw:truncate">{{ fileName }}</span>

            <!-- Spinner durante subida -->
            <svg v-if="uploading" class="tw:w-4 tw:h-4 tw:animate-spin tw:text-blue-500 tw:ml-auto tw:shrink-0" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle class="tw:opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path class="tw:opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>

            <!-- Check si ya subió -->
            <svg v-if="!uploading && uploaded" class="tw:w-4 tw:h-4 tw:text-green-500 tw:ml-auto tw:shrink-0" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>

            <!-- Botón X para quitar el archivo -->
            <button
                type="button"
                class="tw:ml-1 tw:p-1 tw:text-gray-400 hover:tw:text-red-500 tw:rounded tw:transition-colors tw:shrink-0"
                :disabled="uploading"
                @click.stop="clearFile"
                title="Quitar imagen"
            >
                <svg class="tw:w-4 tw:h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>

        <!-- Error message -->
        <p
            v-if="errorMsg"
            class="tw:text-sm tw:text-red-500 tw:font-medium"
        >
            {{ errorMsg }}
        </p>
    </div>
</template>

<script setup>
    import { ref, computed } from 'vue';

    const props = defineProps({
        url: {
            type: String,
            required: true,
        },
        width: {
            type: Number,
            default: 1020,
        },
        height: {
            type: Number,
            default: 340,
        },
        maxSize: {
            type: Number,
            default: 512000,
        },
        accept: {
            type: String,
            default: 'image/jpeg,image/png,image/webp',
        },
    });

    const emit = defineEmits(['success', 'error', 'clear']);

    const fileInput = ref(null);
    const preview = ref(null);
    const uploading = ref(false);
    const uploaded = ref(false);
    const fileName = ref('');
    const errorMsg = ref(null);
    const dragging = ref(false);

    let dragCounter = 0;

    // ── Computed ────────────────────────────────────────────

    const formatAccept = computed(() => {
        return props.accept.split(',').map(s => s.trim()).join(', ');
    });

    const formattedMaxSize = computed(() => {
        const kb = Math.round(props.maxSize / 1024);
        if (kb >= 1024) return `${Math.round(kb / 1024)} MB`;
        return `${kb} KB`;
    });

    const zoneClasses = computed(() => {
        const hasError = errorMsg.value;
        const isDragging = dragging.value;

        return {
            // Base
            'tw:border-2 tw:border-dashed tw:rounded-lg tw:cursor-pointer tw:transition-all tw:duration-200 tw:p-4 dropzone-trigger': true,
            // Default state
            'tw:border-gray-300 tw:bg-gray-50 hover:tw:border-blue-700 hover:tw:bg-blue-100 focus:tw:border-blue-700 focus:tw:ring-2 focus:tw:ring-blue-700': !hasError && !isDragging,
            // Drag-over highlight
            'tw:border-blue-400 tw:bg-blue-50': isDragging && !hasError,
            // Error state
            'tw:border-red-400 tw:bg-red-50': hasError,
        };
    });

    // ── File dialog trigger ─────────────────────────────────

    function triggerFileInput() {
        if (!uploading.value) {
            fileInput.value?.click();
        }
    }

    function clearFile() {
        preview.value = null;
        uploading.value = false;
        uploaded.value = false;
        fileName.value = '';
        errorMsg.value = null;
        emit('clear');
    }

    // ── Drag-and-drop handlers ──────────────────────────────

    function onDragOver() {
        // .prevent applied in template; no-op here.
        // dragover fires continuously, keeping dragging=true
    }

    function onDragEnter() {
        dragCounter++;
        dragging.value = true;
    }

    function onDragLeave() {
        dragCounter--;
        if (dragCounter === 0) {
            dragging.value = false;
        }
    }

    function onDrop(event) {
        dragCounter = 0;
        dragging.value = false;

        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    }

    function onFileSelected(event) {
        const files = event.target.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
        // Reset so the same file can be re-selected
        event.target.value = '';
    }

    // ── File validation + processing ─────────────────────────

    function handleFile(file) {
        errorMsg.value = null;
        uploaded.value = false;
        fileName.value = file.name;

        // 1. Validate MIME type
        const allowedTypes = props.accept.split(',').map(s => s.trim());
        if (file.type && !allowedTypes.includes(file.type)) {
            const msg = 'Formato no soportado. Usa: ' + allowedTypes.join(', ');
            errorMsg.value = msg;
            emit('error', msg);
            return;
        }

        // 2. Validate file size
        if (file.size > props.maxSize) {
            const msg = `El archivo es demasiado grande. Máximo: ${formattedMaxSize.value}`;
            errorMsg.value = msg;
            emit('error', msg);
            return;
        }

        // 3. Read file → validate dimensions → preview → upload
        const reader = new FileReader();

        reader.onload = event => {
            const dataUrl = event.target.result;

            const img = new Image();

            img.onload = () => {
                // Dimension validation (minimum)
                if (img.width < props.width || img.height < props.height) {
                    const msg = `La imagen debe medir al menos ${props.width}×${props.height}px (actual: ${img.width}×${img.height}px)`;
                    errorMsg.value = msg;
                    emit('error', msg);
                    return;
                }

                preview.value = dataUrl;
                startUpload(file);
            };

            img.onerror = () => {
                const msg = 'No se pudo leer la imagen. Verifica que sea un archivo válido.';
                errorMsg.value = msg;
                emit('error', msg);
            };

            img.src = dataUrl;
        };

        reader.onerror = () => {
            const msg = 'Error al leer el archivo.';
            errorMsg.value = msg;
            emit('error', msg);
        };

        reader.readAsDataURL(file);
    }

    // ── XHR Upload ──────────────────────────────────────────

    function startUpload(file) {
        uploading.value = true;
        errorMsg.value = null;

        const formData = new FormData();
        formData.append('file', file);

        const xhr = new XMLHttpRequest();
        xhr.open('POST', props.url);
        xhr.setRequestHeader('Accept', 'application/json');

        const hashTeatro = localStorage.getItem('hash_teatro');
        if (hashTeatro) {
            xhr.setRequestHeader('Hash-Teatro', hashTeatro);
        }

        xhr.onload = () => {
            uploading.value = false;

            if (xhr.status >= 200 && xhr.status < 300) {
                let response;
                try {
                    response = JSON.parse(xhr.responseText);
                } catch {
                    response = xhr.responseText;
                }
                emit('success', file, response);
                uploaded.value = true;
            } else {
                const msg = 'Error del servidor al subir la imagen. Inténtalo de nuevo.';
                errorMsg.value = msg;
                emit('error', msg);
            }
        };

        xhr.onerror = () => {
            uploading.value = false;
            const msg = 'Error de conexión al subir la imagen.';
            errorMsg.value = msg;
            emit('error', msg);
        };

        xhr.ontimeout = () => {
            uploading.value = false;
            const msg = 'La subida tardó demasiado. Inténtalo de nuevo.';
            errorMsg.value = msg;
            emit('error', msg);
        };

        xhr.send(formData);
    }
</script>

<style scoped>
    .dropzone-trigger {
        transition: border-color 0.2s, background-color 0.2s;
    }
    .dropzone-trigger:hover {
        border-color: #1d4ed8 !important;
        background-color: #eff6ff !important;
    }
    .dropzone-trigger:focus-visible {
        border-color: #1d4ed8 !important;
        outline: none !important;
        box-shadow: 0 0 0 3px rgba(29, 78, 216, 0.3) !important;
    }
</style>
